import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DATA_ELEMENT_TYPES } from "@/utils/dataTypes";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import { DataElement, ElementVersion, ReferenceList } from "@/entities/all";
import { useAuth } from "@/contexts/AuthContext";

export default function NewElementModal({ isOpen, onClose, onElementCreated }) {
  const { user } = useAuth();

  const getInitialFormData = () => ({
    // Element fields
    canonical_name: '',
    is_public: false,
    // Version fields
    definition: '',
    data_type: 'string',
    processing_logic: '',
    processing_language: 'python',
    test_cases: [],
    other_attributes: {},
    status: 'DRAFT',
    reference_list_id: null,
    confidence_score: 0.95,
    creation_method: 'MANUAL',
    creation_details: {},
    completeness_weight: 1,
    timeliness_weight: 1
  });

  const [formData, setFormData] = useState(getInitialFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referenceLists, setReferenceLists] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchLists() {
      const lists = await ReferenceList.list();
      setReferenceLists(lists);
    }
    fetchLists();
  }, []);

  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormData());
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let payload = { ...formData };

      // Ensure JSON fields are objects
      try {
        if (typeof payload.other_attributes === 'string' && payload.other_attributes.trim()) {
          payload.other_attributes = JSON.parse(payload.other_attributes);
        } else if (typeof payload.other_attributes === 'string') {
          payload.other_attributes = {};
        }
      } catch {
        setError({ message: '"Other Attributes" is not valid JSON.' });
        setIsSubmitting(false);
        return;
      }
      try {
        if (typeof payload.creation_details === 'string' && payload.creation_details.trim()) {
          payload.creation_details = JSON.parse(payload.creation_details);
        } else if (typeof payload.creation_details === 'string') {
          payload.creation_details = {};
        }
      } catch {
        setError({ message: '"Creation Details" is not valid JSON.' });
        setIsSubmitting(false);
        return;
      }

      // 1. Create DataElement
      const newElement = await DataElement.create({
        canonical_name: payload.canonical_name,
        is_public: payload.is_public,
        current_version_number: 1,
      });
      const userName = user?.first_name + ' ' + user?.last_name || user?.email || 'Unknown User';

      // 2. Create ElementVersion
      const versionPayload = {
        element_id: newElement.id,
        version_number: 1,
        changed_by: userName,
        definition: payload.definition,
        data_type: payload.data_type,
        processing_logic: payload.processing_logic,
        processing_language: payload.processing_language,
        test_cases: payload.test_cases,
        other_attributes: payload.other_attributes,
        status: payload.status,
        reference_list_id: payload.reference_list_id,
        confidence_score: payload.confidence_score,
        creation_method: payload.creation_method,
        creation_details: payload.creation_details,
        completeness_weight: payload.completeness_weight,
        timeliness_weight: payload.timeliness_weight
      };

      await ElementVersion.create(versionPayload);

      onElementCreated(newElement);
    } catch (error) {
      console.error('Error creating new element:', error);
      setError({ message: error.message || 'An error occurred while creating the element.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleJsonChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Data Element</DialogTitle>
          <DialogDescription>
            Define a new canonical element and its initial version.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
            <div className="flex">
              <div className="text-sm text-red-700">{error.message}</div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="p-4 border rounded-lg bg-slate-50 space-y-4">
             <h3 className="text-lg font-semibold text-slate-800">Core Element Properties</h3>
              <div>
                <Label htmlFor="canonical_name">Canonical Name *</Label>
                <Input
                  id="canonical_name"
                  value={formData.canonical_name}
                  onChange={(e) => handleChange('canonical_name', e.target.value)}
                  required
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="is_public_new">Is Public</Label>
                  <p className="text-[0.8rem] text-muted-foreground">
                    Make this element visible in external portals.
                  </p>
                </div>
                <Switch
                  id="is_public_new"
                  checked={formData.is_public}
                  onCheckedChange={(checked) => handleChange('is_public', checked)}
                />
              </div>
          </div>

          <h3 className="text-lg font-semibold text-slate-800 pt-2 border-t mt-6">Initial Version Details (v1)</h3>
          <div className="space-y-4">
              <div>
                <Label htmlFor="definition_new">Definition *</Label>
                <Textarea id="definition_new" value={formData.definition} onChange={(e) => handleChange('definition', e.target.value)} placeholder="Detailed business definition..." rows={3} required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <Label htmlFor="data_type_new">Data Type *</Label>
                    <Select value={formData.data_type} onValueChange={(value) => handleChange('data_type', value)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DATA_ELEMENT_TYPES.map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                  <Label htmlFor="completeness_weight_new">Completeness Weight</Label>
                  <Select value={String(formData.completeness_weight ?? 1)} onValueChange={(v) => handleChange('completeness_weight', parseInt(v))}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0 - Optional (Low)</SelectItem>
                      <SelectItem value="1">1 - Standard (Medium)</SelectItem>
                      <SelectItem value="2">2 - Critical (High)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Field importance for completeness scoring</p>
                </div>
                <div>
                  <Label htmlFor="timeliness_weight_new">Timeliness Weight</Label>
                  <Select value={String(formData.timeliness_weight ?? 1)} onValueChange={(v) => handleChange('timeliness_weight', parseInt(v))}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0 - Static (Not Time-Sensitive)</SelectItem>
                      <SelectItem value="1">1 - Moderate (Time-Sensitive)</SelectItem>
                      <SelectItem value="2">2 - High (Highly Time-Sensitive)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Time sensitivity for timeliness scoring</p>
                </div>
                 <div>
                  <Label htmlFor="status_new">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="PENDING_REVIEW">Pending Review</SelectItem>
                      <SelectItem value="APPROVED">Approved</SelectItem>
                      <SelectItem value="DEPRECATED">Deprecated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="confidence_score_new">Confidence Score</Label>
                  <Input id="confidence_score_new" type="number" step="0.01" min="0" max="1" value={formData.confidence_score} onChange={(e) => handleChange('confidence_score', parseFloat(e.target.value))} placeholder="0.00 to 1.00"/>
                </div>
              </div>
              <div>
                <Label htmlFor="reference_list_id_new">Reference List</Label>
                <Select value={formData.reference_list_id} onValueChange={(value) => handleChange('reference_list_id', value === 'none' ? null : Number(value))}>
                  <SelectTrigger><SelectValue placeholder="Select reference list (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {referenceLists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>{list.list_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Creating...' : 'Create Element & Version'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
